/// <amd-module name="Repos/FilesHubCommon/FilesHubTabs" />
import { IGitItem } from "Repos/Files/Contracts";
import { IFilesHubCommonService } from "Repos/FilesHubCommon/Contracts";
import * as Resources from "Repos/FilesHubCommon/Resources";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { equals } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssPageContext, IVssService, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { IVssContributedTab } from "VSSUI/Tabs";

export interface IFilesHubTabRegistration {
    /**
     * Set of dependencies that should be pre-fetched when this tab is selected. WrappedComponents within
     * tabs will ensure that dependencies are resolved, but tabs may not render wrapped components until
     * after they have fetched file content. This issues the file-content and tab-content requests in
     * parallel, avoiding the back-to-back round trips.
     */
    dependencies?: string[];

    /**
     * Method to get the tab given the current state of the hub. This can return undefined to indicate
     * that the tab is not relevant for the current state.
     *
     * @param pageContext Page context
     * @param item Selected item
     * @param editMode True if in edit mode
     */
    getTab(pageContext: IVssPageContext, item: IGitItem, editMode: boolean): IVssContributedTab | undefined;

    /**
     * Method to decide whether the specified tab should be selected for the currently selected item.
     * @param pageContext Page context
     * @param item Selected item
     * @param editMode True if in edit mode
     * @param tab Current tab
     * @param currentTabId Id of the currently selected tab
     */
    isDefaultTab?(pageContext: IVssPageContext, item: IGitItem, editMode: boolean, tab: IVssContributedTab, currentTabId: string): boolean;
}

export interface IFilesHubTabsService extends IVssService {
    getSelectedTabId(): IReadonlyObservableValue<string>;
    getTabs(): IReadonlyObservableArray<IVssContributedTab>;
    setSelectedTabId(id: string): void;
}

interface ITabRegistrationPair {
    registration: IFilesHubTabRegistration;
    tab: IVssContributedTab;
}

export abstract class FilesHubTabsServiceBase extends VssService implements IFilesHubTabsService {
    private selectedTabId: ObservableValue<string>;
    private tabs: ObservableCollection<IVssContributedTab>;
    private firstPartyTabs: ObservableArray<IVssContributedTab>;
    private firstPartyTabPairs: ITabRegistrationPair[] = [];
    private thirdPartyTabs: IReadonlyObservableArray<IVssContributedTab>;
    private cleanup: () => void;

    protected loadedFolders: IReadonlyObservableArray<{}>;
    protected selectedItem: IReadonlyObservableValue<IGitItem>;
    protected editMode: IReadonlyObservableValue<boolean>;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const filesHubService = this.getFilesHubService(pageContext);
        this.selectedItem = filesHubService.getSelectedItem();
        this.selectedItem.subscribe(this.onSelectedItemChanged);

        this.editMode = filesHubService.getEditMode();
        this.editMode.subscribe(this.onEditModeChanged);

        this.loadedFolders = filesHubService.getLoadedFolders();
        if (this.loadedFolders.length === 0) {
            // If data has not been loaded yet, selectedItem is temporary, so not reliable.
            // When initial data comes, we'll re-evaluate tabs.
            this.loadedFolders.subscribe(this.onLoadedFoldersChanged);
        }

        const pageHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const historyState = pageHistoryService.getProjectedState();
        this.selectedTabId = new ObservableValue(historyState["_a"] || "");
        pageHistoryService.registerObservable(
            this.selectedTabId,
            "_a",
            HistoryBehavior.replace,
            value => (value === "contents" ? "" : value),
            fromUrl => fromUrl || ""
        );

        this.firstPartyTabs = new ObservableArray();
        this.thirdPartyTabs = getTabs(pageContext, "ms.vss-code-web.file-explorer-tabs");
        this.computeTabs();

        this.tabs = new ObservableCollection();
        this.tabs.push(this.firstPartyTabs);
        this.tabs.push(this.thirdPartyTabs);

        this.selectedTabId.subscribe(this.onSelectedTabChanged);

        const shortcutService = pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        shortcutService.registerShortcut(Resources.FilesShortcutGroup, {
            combos: ["1"],
            description: Resources.Contents,
            action: () => {
                this.setSelectedTabId("contents");
            }
        });
        shortcutService.registerShortcut(Resources.FilesShortcutGroup, {
            combos: ["2"],
            description: Resources.History,
            action: () => {
                this.setSelectedTabId("history");
            }
        });

        this.cleanup = () => {
            shortcutService.unRegisterShortcut(Resources.FilesShortcutGroup, "1");
            shortcutService.unRegisterShortcut(Resources.FilesShortcutGroup, "2");
        };
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);

        this.selectedItem.unsubscribe(this.onSelectedItemChanged);
        this.editMode.unsubscribe(this.onEditModeChanged);
        this.loadedFolders.unsubscribe(this.onLoadedFoldersChanged);
        this.selectedTabId.unsubscribe(this.onSelectedTabChanged);
        this.cleanup();
    }

    public getSelectedTabId(): IReadonlyObservableValue<string> {
        return this.selectedTabId;
    }

    public getTabs(): IReadonlyObservableArray<IVssContributedTab> {
        return this.tabs;
    }

    public setSelectedTabId(id: string): void {
        this.selectedTabId.value = id;
    }

    protected abstract getTabRegistrations(): IFilesHubTabRegistration[];
    protected abstract getFilesHubService(pageContext: IVssPageContext): IFilesHubCommonService;

    private updateFirstPartyTabs(item: IGitItem, editMode: boolean): void {
        if (this.loadedFolders.length === 0) {
            // If no initial data loaded yet, current tabs are not reliable, so don't render any yet.
            return;
        }

        const tabs: ITabRegistrationPair[] = [];

        for (const registration of this.getTabRegistrations()) {
            const tab = registration.getTab(this.pageContext, item, editMode);
            if (tab) {
                const pair = { tab, registration };
                tabs.push(pair);
            }
        }

        tabs.sort((t1, t2) => (t1.tab.order || 100) - (t2.tab.order || 100));
        this.firstPartyTabPairs = tabs;
        this.firstPartyTabs.value = tabs.map(t => t.tab);
    }

    private processTabSelection(item: IGitItem, editMode: boolean, resetSelectedTab?: boolean): void {
        if (this.loadedFolders.length === 0) {
            // If no initial data loaded yet, current tabs are not reliable, so don't coerce selected tab yet.
            return;
        }

        if (this.firstPartyTabPairs.length) {
            // When the selected item changes:
            //   - First see any default tab exists
            //   - If there is no default tab, see the selected tab exists in the available
            //     tabs. If it doesn't exist go with the first available
            //   - Change the selected tab only if necessary
            let newTabPair = this.firstPartyTabPairs.filter(
                p => p.registration.isDefaultTab && p.registration.isDefaultTab(this.pageContext, item, editMode, p.tab, this.selectedTabId.value)
            )[0];

            if (!newTabPair && (resetSelectedTab || !this.firstPartyTabPairs.some(p => equals(this.selectedTabId.value, p.tab.id)))) {
                newTabPair = this.firstPartyTabPairs[0];
            }

            if (newTabPair && newTabPair.tab.id !== this.selectedTabId.value) {
                this.selectedTabId.value = newTabPair.tab.id;
            }
        }
    }

    private prefetchTabDependencies(): void {
        let selectedTabRegistration: IFilesHubTabRegistration | undefined;
        for (const pair of this.firstPartyTabPairs) {
            if (equals(pair.tab.id, this.selectedTabId.value, true)) {
                selectedTabRegistration = pair.registration;
                break;
            }
        }

        // Prefetch any tab dependencies
        if (selectedTabRegistration && selectedTabRegistration.dependencies) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getContributionsAsync(selectedTabRegistration.dependencies);
        }
    }

    private onSelectedTabChanged = () => {
        this.prefetchTabDependencies();
        	
        // After initial load, we should never keep an empty tabId, which would render empty tab content.
        if (!this.selectedTabId.value) {
            this.processTabSelection(this.selectedItem.value, this.editMode.value);
        }
    };

    private onSelectedItemChanged = () => {
        this.computeTabs();
    };

    private onEditModeChanged = (editMode: boolean) => {
        // Reset the selected tab when switching into edit mode
        // (ignoring which tab we were previously on so that we can default into the editor)
        this.computeTabs(editMode);
    };

    private onLoadedFoldersChanged = () => {
        if (this.loadedFolders.length > 0) {
            this.computeTabs();
            this.loadedFolders.unsubscribe(this.onLoadedFoldersChanged);
        }
    };

    private computeTabs(resetSelectedTab?: boolean) {
        const item = this.selectedItem.value;
        const editing = this.editMode.value;
        this.updateFirstPartyTabs(item, editing);
        this.processTabSelection(item, editing, resetSelectedTab);
        this.prefetchTabDependencies();
    }
}
